import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {resolveApiBase} from '../core/api-base';
import {environment} from '../../environments/environment';
import {RecaptchaService} from './recaptcha.service';
import {AltchaSolver} from './altcha-solver.service';

/**
 * The discovery document's `captcha` member (Story 54.4): which captcha this
 * deployment runs, delivered at runtime. Present only when captcha_required.
 */
export interface CaptchaConfig {
  provider: string;
  site_key?: string;
  challenge_path?: string;
}

/** The selected provider's request field and its token. */
export interface CaptchaToken {
  field: 'recaptcha_token' | 'altcha_payload';
  value: string;
}

export type CaptchaFlow = 'login' | 'register';

/**
 * Story 54.4 — one facade over both captcha providers. The provider comes
 * from discovery at runtime, so a rollback is a configuration change, not a
 * build. Only the selected provider's field is ever posted (AC6).
 *
 * ALTCHA: a FRESH challenge is fetched on every call — at submit, never at
 * page load — because a challenge is single-use and expires in 120 s; reusing
 * one after a failed submit (wrong password → retry) would be a replay 403.
 *
 * Any failure rejects; the caller shows the captcha error and does not submit.
 */
@Injectable({providedIn: 'root'})
export class CaptchaService {
  private readonly http = inject(HttpClient);
  private readonly recaptcha = inject(RecaptchaService);
  private readonly altcha = inject(AltchaSolver);

  async solve(flow: CaptchaFlow, clientId: string, config: CaptchaConfig | undefined): Promise<CaptchaToken> {
    // An older discovery without the member (mid-rollout) is reCAPTCHA with
    // the build-time key — what it was before 54.4.
    const provider = config?.provider ?? 'recaptcha';
    if (provider === 'altcha') {
      if (!config?.challenge_path) {
        throw new Error('ALTCHA selected without a challenge path');
      }
      // The widget download runs alongside the challenge fetch (the challenge
      // TTL must not run down during a cold load; the no-op catch only avoids
      // an unhandled rejection if the fetch fails first — solve() still throws).
      const loading = this.altcha.load();
      loading.catch(() => undefined);
      const url = `${resolveApiBase()}${config.challenge_path}`
        + `?flow=${encodeURIComponent(flow)}&client_id=${encodeURIComponent(clientId)}`;
      const challenge = await firstValueFrom(this.http.get<unknown>(url));
      await loading;
      return {field: 'altcha_payload', value: await this.altcha.solve(challenge)};
    }
    if (provider === 'recaptcha') {
      const siteKey = config?.site_key || environment.recaptchaSiteKey;
      return {field: 'recaptcha_token', value: await this.recaptcha.execute(flow, siteKey)};
    }
    throw new Error(`unsupported captcha provider: ${provider}`);
  }
}
